import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../config';
import { Trailer } from '../../models/types';

const COLLECTION_NAME = 'trailers';

// Get all trailers
export const getTrailers = async (): Promise<Trailer[]> => {
  try {
    const trailersRef = collection(db, COLLECTION_NAME);
    const snapshot = await getDocs(trailersRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        vin: data.vin || '',
        licensePlate: data.licensePlate || '',
        manufacturer: data.manufacturer || '',
        model: data.model || '',
        year: data.year || 0,
        capacity: data.capacity || 0,
        placedInServiceDate: data.placedInServiceDate?.toDate() || new Date(),
        lastMaintenanceDate: data.lastMaintenanceDate?.toDate(),
        nextMaintenanceDate: data.nextMaintenanceDate?.toDate(),
        assignedVesselId: data.assignedVesselId || '',
        assignedVesselName: data.assignedVesselName || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        status: data.status || 'active',
        notes: data.notes || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    });
  } catch (error) {
    console.error('Error getting trailers:', error);
    throw error;
  }
};

// Get a single trailer by ID
export const getTrailerById = async (id: string): Promise<Trailer | null> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, id);
    const trailerDoc = await getDoc(trailerRef);
    
    if (!trailerDoc.exists()) {
      return null;
    }
    
    const data = trailerDoc.data();
    return {
      id: trailerDoc.id,
      vin: data.vin || '',
      licensePlate: data.licensePlate || '',
      manufacturer: data.manufacturer || '',
      model: data.model || '',
      year: data.year || 0,
      capacity: data.capacity || 0,
      placedInServiceDate: data.placedInServiceDate?.toDate() || new Date(),
      lastMaintenanceDate: data.lastMaintenanceDate?.toDate(),
      nextMaintenanceDate: data.nextMaintenanceDate?.toDate(),
      assignedVesselId: data.assignedVesselId || '',
      assignedVesselName: data.assignedVesselName || '',
      isActive: data.isActive !== undefined ? data.isActive : true,
      status: data.status || 'active',
      notes: data.notes || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  } catch (error) {
    console.error('Error getting trailer:', error);
    throw error;
  }
};

// Get trailers assigned to a specific vessel
export const getTrailersByVesselId = async (vesselId: string): Promise<Trailer[]> => {
  try {
    const trailersRef = collection(db, COLLECTION_NAME);
    const q = query(trailersRef, where('assignedVesselId', '==', vesselId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        vin: data.vin || '',
        licensePlate: data.licensePlate || '',
        manufacturer: data.manufacturer || '',
        model: data.model || '',
        year: data.year || 0,
        capacity: data.capacity || 0,
        placedInServiceDate: data.placedInServiceDate?.toDate() || new Date(),
        lastMaintenanceDate: data.lastMaintenanceDate?.toDate(),
        nextMaintenanceDate: data.nextMaintenanceDate?.toDate(),
        assignedVesselId: data.assignedVesselId || '',
        assignedVesselName: data.assignedVesselName || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        status: data.status || 'active',
        notes: data.notes || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    });
  } catch (error) {
    console.error('Error getting trailers by vessel:', error);
    throw error;
  }
};

// Create a new trailer
export const createTrailer = async (trailer: Omit<Trailer, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const trailersRef = collection(db, COLLECTION_NAME);
    const docRef = await addDoc(trailersRef, {
      ...trailer,
      placedInServiceDate: Timestamp.fromDate(trailer.placedInServiceDate),
      lastMaintenanceDate: trailer.lastMaintenanceDate ? Timestamp.fromDate(trailer.lastMaintenanceDate) : null,
      nextMaintenanceDate: trailer.nextMaintenanceDate ? Timestamp.fromDate(trailer.nextMaintenanceDate) : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating trailer:', error);
    throw error;
  }
};

// Update an existing trailer
export const updateTrailer = async (id: string, trailer: Partial<Trailer>): Promise<void> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, id);
    const updateData: any = { ...trailer };
    
    // Convert Date objects to Firestore Timestamps
    if (trailer.placedInServiceDate) {
      updateData.placedInServiceDate = Timestamp.fromDate(trailer.placedInServiceDate);
    }
    
    if (trailer.lastMaintenanceDate) {
      updateData.lastMaintenanceDate = Timestamp.fromDate(trailer.lastMaintenanceDate);
    }
    
    if (trailer.nextMaintenanceDate) {
      updateData.nextMaintenanceDate = Timestamp.fromDate(trailer.nextMaintenanceDate);
    }
    
    updateData.updatedAt = serverTimestamp();
    
    await updateDoc(trailerRef, updateData);
  } catch (error) {
    console.error('Error updating trailer:', error);
    throw error;
  }
};

// Delete a trailer (now sets status to deleted)
export const deleteTrailer = async (id: string): Promise<void> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(trailerRef, {
      status: 'deleted',
      isActive: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting trailer:', error);
    throw error;
  }
};

// Assign a trailer to a vessel
export const assignTrailerToVessel = async (trailerId: string, vesselId: string, vesselName: string): Promise<void> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, trailerId);
    await updateDoc(trailerRef, {
      assignedVesselId: vesselId,
      assignedVesselName: vesselName,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error assigning trailer to vessel:', error);
    throw error;
  }
};

// Unassign a trailer from a vessel
export const unassignTrailerFromVessel = async (trailerId: string): Promise<void> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, trailerId);
    await updateDoc(trailerRef, {
      assignedVesselId: '',
      assignedVesselName: '',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error unassigning trailer from vessel:', error);
    throw error;
  }
};

// Update trailer maintenance information
export const updateTrailerMaintenance = async (
  trailerId: string,
  lastMaintenanceDate: Date,
  nextMaintenanceDate: Date,
  maintenanceType: string
): Promise<void> => {
  try {
    const trailerRef = doc(db, COLLECTION_NAME, trailerId);
    await updateDoc(trailerRef, {
      lastMaintenanceDate: Timestamp.fromDate(lastMaintenanceDate),
      nextMaintenanceDate: Timestamp.fromDate(nextMaintenanceDate),
      maintenanceType,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating trailer maintenance:', error);
    throw error;
  }
}; 